import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Plus, Send, Trash2, Pencil, Check, X, Loader2, MessageSquare, Stethoscope,
} from "lucide-react";

const SUGGESTIONS = [
  "Paediatric amoxicillin dose for a 12 kg child with acute otitis media",
  "First-line management of newly diagnosed type 2 diabetes in an adult",
  "Safe antihypertensive options in pregnancy with doses",
  "Red flags in an adult with acute low back pain",
];

function inline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function renderRich(text) {
  const lines = (text || "").split("\n");
  return lines.map((line, i) => {
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    if (bullet) return <li key={i} className="ml-5 list-disc marker:text-[#1E5AA8] mb-0.5">{inline(bullet[1])}</li>;
    if (!line.trim()) return <div key={i} className="h-2" />;
    const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (heading) return <p key={i} className="font-semibold text-slate-900 mt-2 mb-1">{inline(heading[1])}</p>;
    return <p key={i} className="mb-1 leading-relaxed">{inline(line)}</p>;
  });
}

export default function AskMe() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try { const r = await api.get("/askme/sessions"); setSessions(r.data); return r.data; }
    catch { return []; }
  }, []);

  const selectSession = useCallback(async (id) => {
    setActiveId(id);
    setLoadingMsgs(true);
    try { const r = await api.get(`/askme/sessions/${id}/messages`); setMessages(r.data); }
    catch { setMessages([]); }
    finally { setLoadingMsgs(false); }
  }, []);

  useEffect(() => {
    (async () => {
      const list = await loadSessions();
      if (list.length) selectSession(list[0].id);
    })();
  }, [loadSessions, selectSession]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const newChat = async () => {
    try {
      const r = await api.post("/askme/sessions", {});
      setSessions((s) => [r.data, ...s]);
      setActiveId(r.data.id);
      setMessages([]);
    } catch { toast.error("Could not start a new chat"); }
  };

  const deleteSession = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/askme/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.id !== id));
      if (activeId === id) { setActiveId(null); setMessages([]); }
      toast.success("Chat deleted");
    } catch { toast.error("Delete failed"); }
  };

  const startRename = (s, e) => { e?.stopPropagation(); setRenamingId(s.id); setRenameValue(s.title || ""); };
  const saveRename = async (id) => {
    const title = renameValue.trim();
    setRenamingId(null);
    if (!title) return;
    try {
      await api.put(`/askme/sessions/${id}`, { title });
      setSessions((s) => s.map((x) => (x.id === id ? { ...x, title } : x)));
    } catch { toast.error("Rename failed"); }
  };

  const deleteMessage = async (mid) => {
    try {
      await api.delete(`/askme/messages/${mid}`);
      setMessages((m) => m.filter((x) => x.id !== mid));
      toast.success("Message deleted");
    } catch { toast.error("Delete failed"); }
  };

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    let sid = activeId;
    const temp = { id: `temp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    try {
      if (!sid) {
        const r = await api.post("/askme/sessions", {});
        sid = r.data.id;
        setActiveId(sid);
        setSessions((s) => [r.data, ...s]);
      }
      setMessages((m) => [...m, temp]);
      const res = await api.post(`/askme/sessions/${sid}/messages`, { content: text });
      setMessages((m) => [...m.filter((x) => x.id !== temp.id), res.data.user_message, res.data.assistant_message]);
      loadSessions();
    } catch {
      setMessages((m) => m.filter((x) => x.id !== temp.id));
      setInput(text);
      toast.error("Could not get a reply. Please try again.");
    } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 h-[calc(100vh-190px)] min-h-[520px]" data-testid="askme-tab">
      {/* Sidebar */}
      <aside className="md:w-72 shrink-0 rounded-2xl bg-white border border-slate-100 shadow-sm flex flex-col overflow-hidden">
        <div className="p-3 border-b border-slate-100">
          <button data-testid="askme-new-chat" onClick={newChat}
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#1E5AA8] text-white text-sm font-semibold hover:bg-[#123C73] transition-colors">
            <Plus className="w-4 h-4" /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-auto p-2 space-y-1" data-testid="askme-session-list">
          {sessions.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No chats yet. Start one above.</p>}
          {sessions.map((s) => (
            <div key={s.id} data-testid={`askme-session-${s.id}`} onClick={() => selectSession(s.id)}
              className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer text-sm transition-colors ${activeId === s.id ? "bg-[#EBF3FB] text-[#123C73]" : "text-slate-600 hover:bg-slate-50"}`}>
              <MessageSquare className="w-4 h-4 shrink-0 text-[#1E5AA8]/70" />
              {renamingId === s.id ? (
                <input data-testid="askme-rename-input" autoFocus value={renameValue}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveRename(s.id); if (e.key === "Escape") setRenamingId(null); }}
                  onBlur={() => saveRename(s.id)}
                  className="flex-1 min-w-0 px-1.5 py-0.5 rounded border border-[#1E5AA8]/40 text-xs focus:outline-none" />
              ) : (
                <span className="flex-1 min-w-0 truncate" data-notranslate>{s.title || "New chat"}</span>
              )}
              <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                <button data-testid={`askme-rename-${s.id}`} onClick={(e) => startRename(s, e)} className="p-1 rounded hover:bg-white text-slate-400 hover:text-[#1E5AA8]"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`askme-delete-session-${s.id}`} onClick={(e) => deleteSession(s.id, e)} className="p-1 rounded hover:bg-white text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-slate-100 text-[11px] text-slate-400 leading-snug">
          Private to you. Answers follow your country's clinical practice & your language.
        </div>
      </aside>

      {/* Chat area */}
      <section className="flex-1 rounded-2xl bg-white border border-slate-100 shadow-sm flex flex-col overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2 bg-gradient-to-r from-[#EBF3FB] to-[#FFFBEB]">
          <div className="w-9 h-9 rounded-xl bg-[#1E5AA8] flex items-center justify-center"><Sparkles className="w-5 h-5 text-amber-300" /></div>
          <div>
            <h2 className="font-display text-lg font-bold text-[#123C73] leading-none">Ask Me</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">Your clinical AI companion — doses, differentials, guidelines & more.</p>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-auto p-4 sm:p-6 space-y-4" data-testid="askme-messages">
          {loadingMsgs ? (
            <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-lg mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-[#EBF3FB] flex items-center justify-center mb-3"><Stethoscope className="w-7 h-7 text-[#1E5AA8]" /></div>
              <h3 className="font-display text-xl font-bold text-slate-800">Ask a clinical question</h3>
              <p className="text-sm text-slate-500 mt-1">Get concise, evidence-based answers with generic drug names and dosing — tailored to your region.</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-5 w-full">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} data-testid={`askme-suggestion-${i}`} onClick={() => send(s)}
                    className="text-left text-xs text-slate-600 border border-slate-200 rounded-xl px-3 py-2.5 hover:border-[#1E5AA8] hover:bg-[#EBF3FB]/40 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} data-testid={`askme-message-${m.id}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`group relative max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-[#1E5AA8] text-white rounded-br-md" : "bg-slate-50 border border-slate-100 text-slate-700 rounded-bl-md"}`}>
                  {m.role === "assistant" ? (
                    <div className="prose-sm" data-notranslate>{renderRich(m.content)}</div>
                  ) : (
                    <p className="whitespace-pre-wrap" data-notranslate>{m.content}</p>
                  )}
                  {!String(m.id).startsWith("temp-") && (
                    <button data-testid={`askme-delete-message-${m.id}`} onClick={() => deleteMessage(m.id)}
                      title="Delete message"
                      className={`absolute -top-2 ${m.role === "user" ? "-left-2" : "-right-2"} opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start" data-testid="askme-typing">
              <div className="rounded-2xl rounded-bl-md bg-slate-50 border border-slate-100 px-4 py-3 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#1E5AA8]/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 rounded-full bg-[#1E5AA8]/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 rounded-full bg-[#1E5AA8]/50 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 p-3">
          <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-[#1E5AA8]/30">
            <textarea data-testid="askme-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
              rows={1} placeholder="Ask a clinical question… (Shift+Enter for a new line)"
              className="flex-1 resize-none max-h-40 bg-transparent text-sm focus:outline-none py-1.5" />
            <button data-testid="askme-send-btn" onClick={() => send()} disabled={sending || !input.trim()}
              className="shrink-0 w-10 h-10 rounded-xl bg-[#1E5AA8] text-white flex items-center justify-center hover:bg-[#123C73] disabled:opacity-40 transition-colors">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-[11px] text-slate-400 text-center mt-1.5">Ask Me supports your clinical judgement — always verify before prescribing.</p>
        </div>
      </section>
    </div>
  );
}
